/** App frame: sidebar (desktop), bottom tabs (mobile), top bar with breadcrumb and engine status (docs/12-ux.md §2). */
import type { ReactNode } from "react";
import { Link, useLocation } from "../lib/router.tsx";
import { useApi } from "../lib/data.ts";
import type { Bot } from "../lib/types.ts";
import { Crew } from "./Crew.tsx";
import { botHealth, Boundary, HealthDot, Icon, ICONS } from "./ui.tsx";

interface Status { engine: { region?: string; connected: boolean; lastSeen: number | null; level: string } }

function NavItem({ to, icon, label, badge, badgeTone = "bg-warn", active, children }: { to: string; icon?: string; label: string; badge?: number; badgeTone?: string; active: boolean; children?: ReactNode }) {
  return (
    <Link to={to} aria-current={active ? "page" : undefined}
      className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[14px] no-underline hover:no-underline ${active ? "bg-line font-semibold text-ink-strong" : "text-ink/85 hover:bg-line/60"}`}>
      {children ?? <>
        <span className="flex w-5 justify-center"><Icon d={icon!} /></span>
        <span className="flex-1 truncate">{label}</span>
        {!!badge && <span className={`rounded-full px-2 text-[12px] font-semibold text-white ${badgeTone}`}>{badge}</span>}
      </>}
    </Link>
  );
}

export function useEngine() {
  const { data } = useApi<Status>("/status", { refreshMs: 30_000 });
  const e = data?.engine;
  return { known: !!e, ok: !!e?.connected || (!!e?.lastSeen && Date.now() - e.lastSeen < 120_000), lastSeen: e?.lastSeen ?? null };
}

/** Says something only when there is something to say. */
function EngineWarning() {
  const e = useEngine();
  if (!e.known || e.ok) return null;
  return <span role="status" className="text-[13px] text-warn-ink">Engine not responding: bots catch up when it's back</span>;
}

export function Shell({ crumb, actions, children }: { crumb: ReactNode; actions?: ReactNode; children: ReactNode }) {
  const { path } = useLocation();
  const { data: bots } = useApi<{ accounts: Bot[] }>("/accounts", { refreshMs: 60_000 });
  const { data: needs } = useApi<{ needs: unknown[] }>("/needs", { refreshMs: 60_000 });
  const nNeeds = needs?.needs.length ?? 0;
  const on = (p: string) => (p === "/" ? path === "/" : path.startsWith(p));
  return (
    <div className="flex min-h-screen">
      <nav aria-label="Main" className="sticky top-0 hidden h-screen w-[232px] shrink-0 flex-col gap-5 overflow-y-auto border-r border-line bg-side px-3 py-5 md:flex">
        <Link to="/" className="flex items-center gap-2.5 px-2 text-ink-strong no-underline hover:no-underline"><Crew character="nebula" size={26} /><span className="text-[20px] font-bold tracking-[-0.02em]">Dukun</span></Link>
        <div className="flex flex-col gap-0.5">
          <NavItem to="/" icon={ICONS.today} label="Today" active={on("/")} />
          <NavItem to="/needs" icon={ICONS.needs} label="Needs you" badge={nNeeds} active={on("/needs")} />
          <NavItem to="/chats" icon={ICONS.chats} label="Chats" active={on("/chats")} />
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="px-2.5 pb-1.5 text-[11px] font-semibold tracking-[0.12em] text-muted">BOTS</span>
          <NavItem to="/bots" icon={ICONS.portfolio} label="Portfolio" active={path === "/bots"} />
          {bots?.accounts.map((b) => (
            <NavItem key={b.id} to={`/bots/${b.id}`} label={b.name} active={path.startsWith(`/bots/${b.id}`)}>
              <Crew character={b.character} size={20} />
              <span className="flex-1 truncate">{b.name}</span>
              <HealthDot h={botHealth(b)} />
            </NavItem>
          ))}
          <NavItem to="/bots/new" icon={ICONS.plus} label="New bot" active={path === "/bots/new"} />
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="px-2.5 pb-1.5 text-[11px] font-semibold tracking-[0.12em] text-muted">STRATEGIES</span>
          <NavItem to="/strategies" icon={ICONS.library} label="Library" active={on("/strategies")} />
          <NavItem to="/backtests" icon={ICONS.backtests} label="Backtests" active={on("/backtests")} />
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="px-2.5 pb-1.5 text-[11px] font-semibold tracking-[0.12em] text-muted">VIEWS</span>
          <NavItem to="/trades" icon={ICONS.trades} label="Trades" active={on("/trades")} />
        </div>
        <div className="flex-1" />
        <div className="border-t border-line pt-3"><NavItem to="/settings" icon={ICONS.settings} label="Settings" active={on("/settings")} /></div>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-line bg-bg/95 px-4 py-3 backdrop-blur md:px-8 md:py-4">
          <div className="flex min-w-0 items-center gap-2 text-[13px] text-muted">
            <Link to="/" className="md:hidden" aria-label="Dukun home"><Crew character="nebula" size={22} /></Link>
            <span className="truncate">{crumb}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2.5"><EngineWarning />{actions}</div>
        </header>
        <Boundary>{children}</Boundary>
      </div>

      <nav aria-label="Tabs" className="fixed inset-x-0 bottom-0 z-20 flex border-t border-line bg-side/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden">
        {([["/", "Today", ICONS.today, nNeeds], ["/bots", "Bots", ICONS.portfolio, 0], ["/strategies", "Strategies", ICONS.library, 0], ["/chats", "Chats", ICONS.chats, 0], ["/settings", "You", ICONS.you, 0]] as const).map(([to, label, icon, badge]) => {
          const active = to === "/" ? path === "/" || path === "/needs" : path.startsWith(to);
          return (
            <Link key={to} to={to} aria-current={active ? "page" : undefined} className={`relative flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] no-underline hover:no-underline ${active ? "font-semibold text-ink-strong" : "text-muted"}`}>
              <Icon d={icon} size={20} />{label}
              {!!badge && <span className="absolute right-[calc(50%-20px)] top-1 rounded-full bg-warn px-1.5 text-[10px] font-semibold text-white">{badge}</span>}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
